import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const TrueFocus = ({
  sentence = "Metagent",
  separator = " ",
  manualMode = false,
  blurAmount = 5,
  borderColor = "#CCFF00",
  glowColor = "rgba(204, 255, 0, 0.6)",
  animationDuration = 0.5,
  pauseBetweenAnimations = 1,
  fontSize = "3vw",
  fontFamily = '"Satoshi", sans-serif',
  fontWeight = "700",
  color = "#ffffff",
}) => {
  const words = separator === "" ? sentence.split("") : sentence.split(separator);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const wordRefs = useRef([]);
  const [focusRect, setFocusRect] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!manualMode) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % words.length);
      }, (animationDuration + pauseBetweenAnimations) * 1000);

      return () => clearInterval(interval);
    }
  }, [manualMode, animationDuration, pauseBetweenAnimations, words.length]);

  useEffect(() => {
    if (currentIndex === null || currentIndex === -1) return;
    if (!wordRefs.current[currentIndex] || !containerRef.current) return;

    const parentRect = containerRef.current.getBoundingClientRect();
    const activeRect = wordRefs.current[currentIndex].getBoundingClientRect();

    setFocusRect({
      x: activeRect.left - parentRect.left,
      y: activeRect.top - parentRect.top,
      width: activeRect.width,
      height: activeRect.height,
    });
  }, [currentIndex, words.length]);

  return (
    <div
      className="true-focus-container"
      ref={containerRef}
      style={{ 
        position: "relative",
        display: "flex",
        gap: "0.5vw",
        justifyContent: "flex-end",
        alignItems: "center",
        flexWrap: "wrap",
        outline: "none",
        userSelect: "none",
        fontFamily: fontFamily,
        transform: "translateZ(0)",
        willChange: "transform",
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        WebkitTransform: "translateZ(0)",
      }}
    >
      {words.map((word, index) => {
        const isActive = index === currentIndex;
        return (
          <span
            key={index}
            ref={(el) => (wordRefs.current[index] = el)}
            className={`true-focus-word ${isActive ? "active" : ""}`}
            style={{
              position: "relative",
              fontSize: fontSize,
              fontWeight: fontWeight,
              color: color,
              cursor: "pointer",
              filter: isActive ? "blur(0px)" : `blur(${blurAmount}px)`,
              transition: `filter ${animationDuration}s ease`,
              outline: "none",
              userSelect: "none",
              letterSpacing: "-0.02em",
              willChange: "filter",
              transform: "translateZ(0)",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              WebkitTransform: "translateZ(0)",
            }}
          >
            {word}
          </span>
        );
      })}

      <motion.div
        className="true-focus-border"
        animate={{
          x: focusRect.x,
          y: focusRect.y,
          width: focusRect.width,
          height: focusRect.height,
          opacity: currentIndex >= 0 ? 1 : 0,
        }}
        transition={{
          duration: animationDuration,
          ease: "easeInOut",
        }}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          boxSizing: "border-box",
          border: "none",
          transform: "translateZ(0)",
          willChange: "transform, opacity",
          backfaceVisibility: "hidden",
          WebkitBackfaceVisibility: "hidden",
          WebkitTransform: "translateZ(0)",
        }}
      >
        {/* 4 corners */}
        <span
          className="true-focus-corner"
          style={{
            position: "absolute",
            width: "16px",
            height: "16px",
            border: "3px solid",
            borderColor: borderColor,
            borderRadius: "3px",
            top: "-10px",
            left: "-10px",
            borderRight: "none",
            borderBottom: "none",
            filter: `drop-shadow(0 0 4px ${borderColor})`,
          }}
        />
        <span
          className="true-focus-corner"
          style={{
            position: "absolute",
            width: "16px",
            height: "16px",
            border: "3px solid",
            borderColor: borderColor,
            borderRadius: "3px",
            top: "-10px",
            right: "-10px",
            borderLeft: "none",
            borderBottom: "none",
            filter: `drop-shadow(0 0 4px ${borderColor})`,
          }}
        />
        <span
          className="true-focus-corner"
          style={{
            position: "absolute",
            width: "16px",
            height: "16px",
            border: "3px solid",
            borderColor: borderColor,
            borderRadius: "3px",
            bottom: "-10px",
            left: "-10px",
            borderRight: "none",
            borderTop: "none",
            filter: `drop-shadow(0 0 4px ${borderColor})`,
          }}
        />
        <span
          className="true-focus-corner"
          style={{
            position: "absolute",
            width: "16px",
            height: "16px",
            border: "3px solid",
            borderColor: borderColor,
            borderRadius: "3px",
            bottom: "-10px",
            right: "-10px",
            borderLeft: "none",
            borderTop: "none",
            filter: `drop-shadow(0 0 4px ${borderColor})`,
          }}
        />
      </motion.div>
    </div>
  );
};

export default TrueFocus;

